import { Link } from "wouter";
import { Bell, CheckCircle2, Moon, Building2, User as UserIcon, Mail, FolderKanban } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useReminders, useReminderAction } from "@/hooks/useReminders";

interface ReminderRow {
  id: string;
  title: string;
  note: string | null;
  scheduled_for: string;
  status: string;
  entity_type: string | null;
  entity_id: string | null;
  recurrence_rule: string | null;
}

const MAX_VISIBLE = 8;

function entityHref(r: ReminderRow): string | null {
  if (!r.entity_type || !r.entity_id) return null;
  switch (r.entity_type) {
    case "company": return `/companies/${r.entity_id}`;
    case "contact": return `/contacts/${r.entity_id}`;
    case "deal": return `/projects/${r.entity_id}`;
    case "buyer": return `/buyers/${r.entity_id}`;
    case "client": return `/clients/${r.entity_id}`;
    case "coi": return `/coi/${r.entity_id}`;
    case "prospect": return `/prospects/${r.entity_id}`;
    case "capital_provider": return `/capital-providers/${r.entity_id}`;
    case "project": return `/projects/${r.entity_id}`;
    case "email": return null; // emails don't have a detail route
    default: return null;
  }
}

function entityIcon(t: string | null) {
  switch (t) {
    case "contact": return UserIcon;
    case "email": return Mail;
    case "project":
    case "deal": return FolderKanban;
    default: return Building2;
  }
}

function bucketOf(scheduledFor: string, now: Date): "overdue" | "today" | "week" | "later" {
  const t = new Date(scheduledFor);
  if (t < now) return "overdue";
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);
  if (t <= todayEnd) return "today";
  const weekEnd = new Date(now);
  const _dow1 = weekEnd.getDay();
  weekEnd.setDate(weekEnd.getDate() + ((7 - _dow1) % 7));
  weekEnd.setHours(23, 59, 59, 999);
  if (t <= weekEnd) return "week";
  return "later";
}

function ReminderRowItem({ r }: { r: ReminderRow }) {
  const { complete, snooze } = useReminderAction();
  const Icon = entityIcon(r.entity_type);
  const href = entityHref(r);

  const Body = (
    <div className="flex items-start gap-2 py-1.5 px-1 rounded hover:bg-accent group">
      <Icon className="h-3.5 w-3.5 mt-1 flex-shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-foreground truncate">{r.title}</p>
        {r.note && <p className="text-[11px] text-muted-foreground truncate">{r.note}</p>}
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
        <button
          aria-label="Complete reminder"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); complete(r.id); }}
          className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary-text"
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
        </button>
        <button
          aria-label="Snooze 1 day"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); snooze(r.id, "1d"); }}
          className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary-text"
        >
          <Moon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );

  return href ? <Link href={href}>{Body}</Link> : Body;
}

function Group({ label, items }: { label: string; items: ReminderRow[] }) {
  if (items.length === 0) return null;
  return (
    <section>
      <h4 className="px-1 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label} <span className="tabular-nums text-muted-foreground/60">({items.length})</span>
      </h4>
      <div>{items.map((r) => <ReminderRowItem key={r.id} r={r} />)}</div>
    </section>
  );
}

export function RemindersRail() {
  const { data, isLoading, isError, refetch } = useReminders({ status: "pending" });
  const reminders = (data as ReminderRow[] | undefined) ?? [];

  if (isLoading) {
    return (
      <div className="space-y-2 p-3">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full rounded" />)}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground" role="alert">
        Couldn't load reminders.{" "}
        <button className="underline" onClick={() => refetch()}>Retry</button>
      </div>
    );
  }

  const now = new Date();
  const weekEnd = new Date(now);
  const _dow2 = weekEnd.getDay();
  weekEnd.setDate(weekEnd.getDate() + ((7 - _dow2) % 7));
  weekEnd.setHours(23, 59, 59, 999);

  const filtered = reminders
    .filter((r) => new Date(r.scheduled_for) <= weekEnd)
    .sort((a, b) => new Date(a.scheduled_for).getTime() - new Date(b.scheduled_for).getTime());

  const overdue = filtered.filter((r) => bucketOf(r.scheduled_for, now) === "overdue");
  const today = filtered.filter((r) => bucketOf(r.scheduled_for, now) === "today");
  const week = filtered.filter((r) => bucketOf(r.scheduled_for, now) === "week");
  const total = overdue.length + today.length + week.length;

  let trimmed = 0;
  let oC = overdue, tC = today, wC = week;
  if (total > MAX_VISIBLE) {
    let budget = MAX_VISIBLE;
    oC = overdue.slice(0, budget); budget -= oC.length;
    tC = today.slice(0, Math.max(0, budget)); budget -= tC.length;
    wC = week.slice(0, Math.max(0, budget));
    trimmed = total - MAX_VISIBLE;
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto p-3 border-t border-border">
      <div className="flex items-center justify-between px-1 pb-1">
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
          <Bell className="inline h-3 w-3 mr-1" /> Reminders ({total})
        </h3>
        <Link href="/reminders" className="text-xs text-muted-foreground hover:text-foreground underline">
          View all
        </Link>
      </div>
      {total === 0 ? (
        <p className="text-sm text-muted-foreground italic px-1 py-2">🔔 No reminders this week.</p>
      ) : (
        <>
          <Group label="Overdue" items={oC} />
          <Group label="Today" items={tC} />
          <Group label="This week" items={wC} />
          {trimmed > 0 && (
            <Link href="/reminders" className="block text-center text-xs text-muted-foreground hover:text-foreground underline mt-2">
              + {trimmed} more
            </Link>
          )}
        </>
      )}
    </div>
  );
}
